import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import {
  ActivityTypeEnum,
  BaseTimeslotBodyInterface,
  OfferInterface,
} from '@sport-activities/sdk';
import isEqual from 'lodash.isequal';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { VtmnButtonVariantEnum } from '~/component/buttons/vtmn-button/enum/vtmn-button-variant.enum';
import { ModalEditTimeslotsComponent } from '~/component/modal-edit-timeslots/modal-edit-timeslots.component';
import { ModalVariantEnum } from '~/component/modal/enums/modal-variant.enum';
import { ModalComponent } from '~/component/modal/modal.component';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { TranslationKeySubPathEnum } from '~/core/enums/translation-key-sub-path.enum';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { repeatUntil } from '~/core/operators/repeat-until.operator';
import { ActivityService } from '~/core/services/activity/activity.service';
import { TitleService } from '~/core/services/title/title.service';
import { PriceValidator } from '~/core/validators/price/price.validator';
import { FormPlansEditionModeEnum } from '~/organizer/shared/components/shared/plans/plans.component';
import { buildOfferIdentifier } from '~/organizer/shared/helpers/offer/build-offer-identifier/build-offer-identifier.helper';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedLocatableService } from '~/organizer/shared/services/locatable/locatable.service';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';
import { TimeslotUpdateService } from '~/organizer/timeslot-edition/services/timeslot-update.service';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';

import { environment } from '~environments/environment';

@Component({
  selector: 'app-organizer-timeslot-edition-offers',
  templateUrl: './offers.component.html',
})
export class OrganizerTimeslotEditionOffersComponent
  implements OnInit, OnDestroy
{
  @ViewChild('stripeModal') public modal: ModalComponent;
  @ViewChild('modalEditTimeslots')
  public modalEditTimeslots: ModalEditTimeslotsComponent;

  private readonly subscriptions = new Subscription();
  public readonly formPlansEditionModeEnum = FormPlansEditionModeEnum;
  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly modalVariantEnum = ModalVariantEnum;
  public readonly tipsVariant = TipsVariantEnum;
  public readonly translationKeySubPath = TranslationKeySubPathEnum;
  public readonly vtmnButtonVariant = VtmnButtonVariantEnum;
  public readonly toggleNameEnum = ToggleNameEnum;

  private stripeRedirectUrl: string;
  private translationKeys: Record<string, string>;

  public activity: Activity;
  public isBirthday: boolean;
  public isLastTimeslot: boolean;
  public isOverlayDisplayed = false;
  public overlayStatus = SuccessStatusEnum.LOADING;
  public plans: FormControl;
  public previousUrl: string;
  public stripeLinkTarget = '_blank';
  public timeslot: Timeslot;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly titleService: TitleService,
    private readonly toggleService: ToggleService,
    private readonly locatableService: OrganizerSharedLocatableService,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly organizerSharedActivityService: OrganizerSharedActivityService,
    private readonly activityService: ActivityService,
    private readonly timeslotUpdateService: TimeslotUpdateService,
    private readonly router: Router,
    private readonly priceValidator: PriceValidator,
    private readonly organizerSharedOrganisationService: OrganizerSharedOrganizationService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    const { timeslotData } = this.activatedRoute.snapshot.data;
    const { timeslot, activity } = timeslotData;

    this.activity = activity;
    this.timeslot = timeslot;
    this.isBirthday =
      activity.type === ('BIRTHDAY' as ActivityTypeEnum.BIRTHDAY);
    this.previousUrl = `/organizer.timeslot.manage/${timeslot.identifier}`;

    this.titleService.setPageTitle('seo.organizer.edit_timeslot.offers.title');
    this.translationKeys = {
      stripe_url: this.localizeRouterService
        .translateRoute('/organizer.onboarding.payment-1')
        .toString(),
    };

    this.plans = new FormControl(this.timeslot.offers, [
      Validators.required,
      this.priceValidator.validatePaymentProvider(
        this.organizerSharedOrganisationService.organization
      ),
    ]);

    this.subscriptions.add(
      this.activityService
        .getTimeslots({
          activityIdentifier: timeslotData.activity.sku,
          startDate: timeslotData.timeslot.startDate,
        })
        .subscribe((upcomingTimeslots) => {
          this.isLastTimeslot = upcomingTimeslots?.length === 1;
        })
    );

    if (!this.organizerSharedOrganisationService.organization.paymentProvider) {
      this.subscriptions.add(
        this.organizerSharedOrganisationService
          .getStripeRedirectionUrl(
            this.organizerSharedOrganisationService.organization?.identifier,
            `${environment.applications.front.url}${this.localizeRouterService
              .translateRoute(
                `/organizer.timeslot.manage/${timeslot.identifier}/organizer.timeslot-edition.offers`
              )
              .toString()}`
          )
          .subscribe({
            next: (data) => {
              if (data?.url) {
                this.stripeRedirectUrl = data?.url;
                this.stripeLinkTarget = this.stripeRedirectUrl && null;
              }
            },
            error: () => {
              this.setDefaultStripeRedirectUrl();
            },
          })
      );
    } else {
      this.setDefaultStripeRedirectUrl();
    }
  }

  private get currency(): string {
    return this.toggleService
      .getVariant(ToggleNameEnum.Currency, 'EUR')
      .toString()
      ?.split(',')?.[0];
  }

  private get formattedOffers(): OfferInterface[] {
    return this.plans.value.map((plan) => ({
      ...plan,
      price: +plan.price,
      description: plan.description || null,
      currency: this.currency,
      identifier: buildOfferIdentifier(plan.name),
    }));
  }

  public get isFormDirty(): boolean {
    return this.plans.dirty;
  }

  public get isFormValid(): boolean {
    return (
      this.plans.hasError('unauthorized_paid_activity') ||
      (this.plans.valid && this.plans.dirty)
    );
  }

  public get partialTimeslotBody(): Partial<BaseTimeslotBodyInterface> {
    return this.getPartialTimeslotOffers(this.formattedOffers);
  }

  public get partialTimeslotUpdates(): { offers: OfferInterface[] } {
    return {
      offers: this.formattedOffers,
    };
  }

  public get stripeUrl(): string {
    return (
      this.stripeRedirectUrl ||
      this.localizeRouterService
        .translateRoute('/organizer.onboarding.payment-1')
        .toString()
    );
  }

  private checkTimeslotUpdate(): Observable<Timeslot> {
    return this.organizerSharedActivityService
      .getTimeslot(this.timeslot.identifier)
      .pipe(
        repeatUntil((timeslot) =>
          isEqual(timeslot.offers, this.formattedOffers)
        )
      );
  }

  private getPartialTimeslotOffers(
    formattedOffers: OfferInterface[]
  ): Partial<BaseTimeslotBodyInterface> {
    return {
      offers: formattedOffers.map((offer) => ({
        ...offer,
        name: [this.locatableService.buildCurrentLocatableBody(offer.name)],
        description: [
          this.locatableService.buildCurrentLocatableBody(offer.description),
        ],
      })),
    };
  }

  public onSubmit(): void {
    this.modalEditTimeslots.openModal();
  }

  public updateAllTimeslotsOffers(): void {
    this.modalEditTimeslots.closeModal();
    this.isOverlayDisplayed = true;

    this.subscriptions.add(
      combineLatest([
        this.activityService
          .getActivityTimeslots(this.activity?.sku?.toString(), {
            startDate: this.timeslot?.startDate?.toString(),
          })
          .pipe(
            mergeMap((timeslots) => {
              return this.finishPatchTimeslot(
                timeslots,
                this.partialTimeslotBody
              );
            })
          ),
        this.checkTimeslotUpdate(),
      ]).subscribe(() => {
        this.overlayStatus = SuccessStatusEnum.SUCCESS;
      })
    );
  }

  public finishPatchTimeslot(timeslots, partialBody): Observable<string[]> {
    return this.organizerSharedActivityService.patchTimeslotBulk(
      this.activity?.sku?.toString(),
      timeslots.map((timeslot) => ({
        ...partialBody,
        identifier: timeslot.identifier,
      }))
    );
  }

  private setDefaultStripeRedirectUrl(): void {
    this.stripeRedirectUrl = this.organizerSharedOrganisationService
      ?.organization?.paymentProviderIdentifier
      ? `https://dashboard.stripe.com/connect/accounts/${this.organizerSharedOrganisationService?.organization?.paymentProviderIdentifier}`
      : 'https://dashboard.stripe.com/settings/connect';
  }

  public updateSelectedTimeslots(): void {
    this.timeslotUpdateService.setPartialTimeslotUpdates(
      this.partialTimeslotUpdates
    );
    this.timeslotUpdateService.setPartialTimeslotBody(this.partialTimeslotBody);

    const translatedPath = this.localizeRouterService.translateRoute([
      '/organizer.timeslot.manage',
      this.timeslot.identifier,
      'organizer.timeslot-edition.offers',
      'organizer.timeslot-edition.select-timeslots',
    ]) as string[];

    this.router.navigate(translatedPath);
  }

  public updateTimeslotOffers(): void {
    this.modalEditTimeslots.closeModal();
    this.isOverlayDisplayed = true;

    this.subscriptions.add(
      this.organizerSharedActivityService
        .patchTimeslot(this.timeslot.identifier, this.partialTimeslotBody)
        .pipe(
          mergeMap(() => {
            return this.checkTimeslotUpdate();
          })
        )
        .subscribe(() => {
          this.overlayStatus = SuccessStatusEnum.SUCCESS;
        })
    );
  }
}
